/**
 * L'écran de scan : ce qu'il sait sans DOM (SCAN-QR § 3.1 et § 3.3), pour être éprouvé en
 * environnement node. Le mode proposé d'office et sa mémoire du jour, la phrase « un scan
 * maintenant → … », le premier retour d'un scan (tiré de la liste du jour gardée sur le
 * téléphone, avant l'accusé de la base), la recherche par nom ou par numéro, le compteur
 * « n ✓ · n en attente · n à revoir », l'annulation du dernier scan (10 s).
 *
 * Rien ici ne décide de ce qui compte : la base tranche chaque scan (§ 4.2) ; le téléphone
 * annonce, puis l'accusé confirme ou corrige dans la liste des derniers scans, jamais sur la
 * carte de l'enfant suivant. PROPOSITION TECHNIQUE, sauf mention.
 */
import { filtrerEnfants, type EnfantDuLien, type LecturePointage, type PlageDuLien } from '../../../lib/garderie/pointage';
import {
  estARevoir,
  estSourceDeGestion,
  heureEnClair,
  libelleDuResultat,
  lireEtiquette,
  numeroSaisi,
  PAS_UNE_ETIQUETTE,
  type EntreeDeFile,
  type ModeDeScan,
  type PlageDuScan,
  type ResultatDuScan,
} from '../../../lib/garderie/scan';

export type Ton = 'vert' | 'orange' | 'rouge' | 'gris';
export interface Retour {
  ton: Ton;
  texte: string;
}

/* ------------------------------------------------------------------------- */
/* Le mode                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Le mode proposé d'office (SCAN-QR § 3.1, 3) : une école qui fait l'appel — une plage du matin
 * ou une heure d'appel, l'ISA — ouvre en « Appel » ; une école qui ne scanne qu'à la sortie — la
 * CSSA — en « Sortie » (VALIDÉ ÉCLOSION, 03/11/2026). Les écoles se reconnaissent à leurs plages
 * (contrat du lot 1), jamais à leur nom.
 */
export function modeParDefaut(plages: readonly PlageDuLien[]): ModeDeScan {
  return plages.some((p) => p.appelA !== null || p.code === 'MATIN') ? 'appel' : 'sortie';
}

/** Ce que le téléphone garde pour la journée : `AAAA-MM-JJ|mode`. */
export function modeAMemoriser(date: string, mode: ModeDeScan): string {
  return `${date}|${mode}`;
}

/**
 * Le mode à l'ouverture : celui posé aujourd'hui sur ce téléphone (le membre de l'équipe à la
 * sortie le pose une fois), sinon le mode proposé d'office. Un mode d'un autre jour est oublié.
 */
export function modeMemorise(valeur: string | null, aujourdHui: string, plages: readonly PlageDuLien[]): ModeDeScan {
  const [date, mode] = (valeur ?? '').split('|');
  if (date === aujourdHui && (mode === 'appel' || mode === 'sortie')) return mode;
  return modeParDefaut(plages);
}

/* ------------------------------------------------------------------------- */
/* Les textes                                                                 */
/* ------------------------------------------------------------------------- */

/** « Léa D. » : le prénom et l'initiale du nom, comme la réponse de la base. */
export function nomCourt(e: { prenom: string; nom: string }): string {
  const initiale = e.nom.trim().charAt(0).toUpperCase();
  return `${e.prenom.trim()}${initiale ? ` ${initiale}.` : ''}`;
}

/** Le numéro tel que l'étiquette l'imprime, avec le séparateur belge des milliers : `1404` → `1.404`. */
export function numeroEnClair(numero: string): string {
  return numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** `HH:MM[:SS]` → « 16h07 ». */
export function heureCourte(heure: string): string {
  return /^\d{2}:\d{2}/.test(heure) ? `${heure.slice(0, 2)}h${heure.slice(3, 5)}` : heure;
}

/**
 * La phrase de l'en-tête, recalculée chaque seconde (SCAN-QR § 3.1, 3) : « Sortie · un scan
 * maintenant → ATL2 », « Un scan maintenant → à vérifier », « Pas de garderie aujourd'hui ».
 */
export function phraseDuMoment(r: PlageDuScan, mode: ModeDeScan, plages: readonly PlageDuLien[] = []): { texte: string; ton: Ton } {
  const libelleMode = mode === 'appel' ? 'Appel' : 'Sortie';
  if ('plage' in r) {
    return { texte: `${libelleMode} · un scan maintenant → ${r.plage.code}${r.apresLaFin ? ' (après la fin)' : ''}`, ton: r.apresLaFin ? 'orange' : 'vert' };
  }
  switch (r.aucune) {
    case 'pas_de_garderie':
      return { texte: 'Pas de garderie aujourd’hui', ton: 'rouge' };
    case 'parti_avant_l_appel': {
      const appel = plages.find((p) => p.appelA)?.appelA ?? null;
      return { texte: `Sortie · avant l’appel${appel ? ` de ${heureCourte(appel)}` : ''} : un scan ne pointe rien`, ton: 'rouge' };
    }
    case 'a_verifier':
    default:
      return { texte: `${libelleMode} · un scan maintenant → à vérifier`, ton: 'orange' };
  }
}

/* ------------------------------------------------------------------------- */
/* L'enfant d'un scan                                                         */
/* ------------------------------------------------------------------------- */

/** L'enfant de la liste du jour qui porte ce numéro QuickSchool (zéros de tête ignorés), ou `null`. */
export function enfantDuNumero(enfants: readonly EnfantDuLien[], numero: string | null): EnfantDuLien | null {
  if (!numero) return null;
  const n = numero.replace(/^0+/, '');
  return enfants.find((e) => e.qsEleveId !== null && e.qsEleveId.replace(/^0+/, '') === n) ?? null;
}

/**
 * « Chercher » (SCAN-QR § 3.1, 6), pour un enfant sans étiquette ou à l'étiquette illisible :
 * un numéro (« 1.404 », « 1404 ») cherche le numéro QuickSchool ; sinon le début du prénom ou du
 * nom, comme la liste. Au plus `max` enfants ; rien pour une recherche vide.
 */
export function chercherUnEnfant(enfants: readonly EnfantDuLien[], recherche: string, max = 8): EnfantDuLien[] {
  const q = recherche.trim();
  if (!q) return [];
  const numero = numeroSaisi(q);
  if (numero) {
    const e = enfantDuNumero(enfants, numero);
    return e ? [e] : [];
  }
  return filtrerEnfants(enfants, 'tous', q).slice(0, max);
}

/* ------------------------------------------------------------------------- */
/* Les scans de la session                                                    */
/* ------------------------------------------------------------------------- */

/**
 * Un scan fait (ou relu de la file) pendant que l'écran est ouvert. Rien de tout cela n'est
 * écrit dans la file (qui ne garde aucun nom d'enfant) : c'est la mémoire de la page.
 */
export interface ScanDeLaSession {
  cle: string;
  mode: ModeDeScan | 'annulation';
  annuleCle: string | null;
  qr: string | null;
  enfantId: string | null;
  plageId: string | null;
  /** `Date.now()` du téléphone au moment du scan. */
  moment: number;
  /** Le premier retour, calculé à l'écran ; `null` pour un scan relu de la file d'une session précédente. */
  apercu: Retour | null;
  /** L'accusé de la base ; `null` tant qu'il n'est pas arrivé. */
  resultat: ResultatDuScan | null;
}

/** Les résultats d'un accusé qui disent l'enfant présent. */
const PRESENT = new Set(['pointe', 'deja_pointe']);

/** Les clés des scans qu'une annulation a effectivement annulés (`annule`). */
function clesAnnulees(session: readonly ScanDeLaSession[]): Set<string> {
  return new Set(session.filter((s) => s.mode === 'annulation' && s.resultat?.resultat === 'annule' && s.annuleCle).map((s) => s.annuleCle as string));
}

/** Les clés des scans pour lesquels une annulation est partie (quel qu'en soit le résultat). */
function clesDejaVisees(session: readonly ScanDeLaSession[]): Set<string> {
  return new Set(session.filter((s) => s.mode === 'annulation' && s.annuleCle).map((s) => s.annuleCle as string));
}

/**
 * Le compteur du bas (SCAN-QR § 3.1, 6) : « 23 ✓ · 2 en attente · 1 à revoir ». ✓ : les scans
 * dont la base a dit l'enfant présent (pointé, ou déjà pointé), hors scans annulés ; en attente :
 * la file de la source sur le téléphone ; à revoir : les refus et « à vérifier ».
 */
export function compterLaSession(session: readonly ScanDeLaSession[], enAttente: number): { pointes: number; enAttente: number; aRevoir: number } {
  const annulees = clesAnnulees(session);
  let pointes = 0;
  let aRevoir = 0;
  for (const s of session) {
    if (!s.resultat) continue;
    if (s.mode !== 'annulation' && PRESENT.has(s.resultat.resultat) && !annulees.has(s.cle)) pointes += 1;
    if (estARevoir(s.resultat)) aRevoir += 1;
  }
  return { pointes, enAttente, aRevoir };
}

/**
 * Déjà pointé, d'après ce que le téléphone sait : la liste du jour le dit (`presences`), ou un
 * scan de cette session pour le même enfant et la même plage n'a pas (encore) été démenti par la
 * base — ni annulé, ni refusé.
 */
export function dejaPointeIci(enfant: EnfantDuLien, plageId: string, session: readonly ScanDeLaSession[], numeroDeLEnfant: (s: ScanDeLaSession) => string | null): boolean {
  if (enfant.presences.includes(plageId)) return true;
  const annulees = clesAnnulees(session);
  return session.some(
    (s) =>
      s.mode !== 'annulation' &&
      s.plageId === plageId &&
      !annulees.has(s.cle) &&
      (s.resultat === null || PRESENT.has(s.resultat.resultat)) &&
      (s.enfantId === enfant.id || (s.enfantId === null && numeroDeLEnfant(s) !== null && numeroDeLEnfant(s) === enfant.qsEleveId)),
  );
}

/**
 * Le premier retour d'un scan, tiré de la liste du jour (SCAN-QR § 3.1, 5) :
 * - vert « Léa D. · 3B · ATL2 » : l'enfant est connu et la plage tourne ;
 * - orange « Léa D. · déjà pointé ATL1 » : la liste (ou un scan de la session) le sait déjà ;
 * - orange « à vérifier » / rouge « pas de garderie », « parti avant l'appel » : la règle de
 *   plage ne donne rien, le scan part quand même (gardé au journal) ;
 * - gris « 1.404 : la base vérifie » : l'enfant manque à la liste du téléphone.
 */
export function apercuDuScan(args: {
  enfant: EnfantDuLien | null;
  numero: string | null;
  plage: PlageDuScan;
  deja: boolean;
  plages?: readonly PlageDuLien[];
}): Retour {
  const { enfant, numero, plage, deja } = args;
  if (!enfant) return { ton: 'gris', texte: `${numero ? numeroEnClair(numero) : 'Cet enfant'} : la base vérifie` };
  const nom = nomCourt(enfant);
  if (!('plage' in plage)) {
    if (plage.aucune === 'pas_de_garderie') return { ton: 'rouge', texte: `${nom} · pas de garderie aujourd’hui : rien n’est pointé` };
    if (plage.aucune === 'parti_avant_l_appel') {
      const appel = (args.plages ?? []).find((p) => p.appelA)?.appelA ?? null;
      return { ton: 'rouge', texte: `${nom} · parti avant l’appel${appel ? ` de ${heureCourte(appel)}` : ''} : rien n’est pointé` };
    }
    return { ton: 'orange', texte: `${nom} · à vérifier (heure hors plage) : rien n’est pointé` };
  }
  const code = plage.plage.code;
  if (deja) return { ton: 'orange', texte: `${nom} · déjà pointé ${code}` };
  return { ton: 'vert', texte: [nom, enfant.classe, `${code}${plage.apresLaFin ? ' (après la fin)' : ''}`].filter(Boolean).join(' · ') };
}

/** Le retour d'un QR qui n'est pas une étiquette de garderie : rouge, rien en file. */
export const RETOUR_ETRANGER: Retour = { ton: 'rouge', texte: PAS_UNE_ETIQUETTE };

/** Le retour quand le téléphone n'a pas pu garder le scan (stockage refusé) : rien en file. */
export const RETOUR_NON_GARDE: Retour = { ton: 'rouge', texte: 'Scan non gardé sur le téléphone : refaites-le, ou passez par la liste' };

export type EtatDuDernier = 'en_attente' | 'confirme' | 'corrige';

/**
 * Ce que montre une ligne des derniers scans : l'accusé de la base s'il est arrivé (il confirme
 * ou corrige le premier retour), sinon le premier retour, sinon (scan relu de la file d'une
 * session précédente) ce que la liste du jour permet d'en dire.
 */
export function retourDuDernier(s: ScanDeLaSession, lecture: LecturePointage | null, numeroDuQr: (qr: string | null) => string | null): Retour & { etat: EtatDuDernier } {
  const plages = lecture?.plages ?? [];
  if (s.resultat && s.resultat.resultat !== 'non_traite') {
    const enfantLocal = !s.resultat.enfant && lecture ? trouverLEnfant(s, lecture, numeroDuQr) : null;
    const r = enfantLocal
      ? { ...s.resultat, enfant: { id: enfantLocal.id, prenom: enfantLocal.prenom, initiale: enfantLocal.nom.trim().charAt(0).toUpperCase(), classe: enfantLocal.classe } }
      : s.resultat;
    const l = libelleDuResultat(r, plages);
    const etat: EtatDuDernier = s.apercu && s.apercu.ton !== l.ton ? 'corrige' : 'confirme';
    return { ...l, etat };
  }
  if (s.apercu) return { ...s.apercu, etat: 'en_attente' };
  if (s.mode === 'annulation') return { ton: 'gris', texte: 'Annulation : la base vérifie', etat: 'en_attente' };
  const enfant = lecture ? trouverLEnfant(s, lecture, numeroDuQr) : null;
  const numero = numeroDuQr(s.qr);
  const plage = plages.find((p) => p.id === s.plageId)?.code ?? null;
  const qui = enfant ? nomCourt(enfant) : numero ? numeroEnClair(numero) : 'Scan';
  return { ton: 'gris', texte: `${qui}${plage ? ` · ${plage}` : ''} : en attente d’envoi`, etat: 'en_attente' };
}

function trouverLEnfant(s: ScanDeLaSession, lecture: LecturePointage, numeroDuQr: (qr: string | null) => string | null): EnfantDuLien | null {
  if (s.enfantId) return lecture.enfants.find((e) => e.id === s.enfantId) ?? null;
  return enfantDuNumero(lecture.enfants, numeroDuQr(s.qr));
}

/** Le délai pendant lequel « Annuler mon dernier scan » est offert (SCAN-QR § 3.1, 6). */
export const DELAI_D_ANNULATION_MS = 10_000;

/**
 * Le scan que « Annuler mon dernier scan » viserait : le dernier scan de la session (hors
 * annulations), fait il y a moins de 10 s, pas déjà visé par une annulation, et pas déjà refusé
 * par la base (un refus n'a rien posé). `null` sinon.
 */
export function scanAnnulable(session: readonly ScanDeLaSession[], maintenant: number, delaiMs = DELAI_D_ANNULATION_MS): ScanDeLaSession | null {
  const scans = session.filter((s) => s.mode !== 'annulation');
  if (scans.length === 0) return null;
  const dernier = scans.reduce((a, b) => (b.moment >= a.moment ? b : a));
  if (maintenant - dernier.moment >= delaiMs || maintenant < dernier.moment - 1000) return null;
  if (clesDejaVisees(session).has(dernier.cle)) return null;
  if (dernier.resultat && dernier.resultat.definitif && !PRESENT.has(dernier.resultat.resultat)) return null;
  return dernier;
}

/**
 * Qui « Annuler mon dernier scan » vise, en clair : l'enfant de la liste du jour (par sa fiche,
 * sinon par le numéro de l'étiquette), sinon celui de l'accusé de la base, sinon le numéro
 * imprimé. Pendant l'appel de 15h45, le dernier scan change toutes les deux secondes : le bouton
 * dit donc toujours quel enfant il retirerait (relecture du 03/11/2026).
 */
export function quiAnnuler(
  s: ScanDeLaSession,
  enfants: readonly EnfantDuLien[],
  numeroDuQr: (qr: string | null) => string | null,
): string {
  const numero = numeroDuQr(s.qr);
  const deLaListe = (s.enfantId ? enfants.find((e) => e.id === s.enfantId) : null) ?? enfantDuNumero(enfants, numero);
  if (deLaListe) return nomCourt(deLaListe);
  const deLAccuse = s.resultat?.enfant;
  if (deLAccuse) return `${deLAccuse.prenom}${deLAccuse.initiale ? ` ${deLAccuse.initiale}.` : ''}`;
  return numero ? `n° ${numeroEnClair(numero)}` : 'le dernier scan';
}

/** Les dix derniers scans, le plus récent d'abord. */
export function dixDerniers(session: readonly ScanDeLaSession[], combien = 10): ScanDeLaSession[] {
  return [...session].sort((a, b) => b.moment - a.moment).slice(0, combien);
}

/* ------------------------------------------------------------------------- */
/* La clé d'un scan                                                           */
/* ------------------------------------------------------------------------- */

/**
 * Une clé neuve (uuid v4) : `crypto.randomUUID` quand le navigateur l'a, sinon tirée de
 * `crypto.getRandomValues`. La même clé suit le scan jusqu'à son résultat définitif.
 */
export function nouvelleCle(c: Pick<Crypto, 'getRandomValues'> & { randomUUID?: () => string } = globalThis.crypto): string {
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const o = c.getRandomValues(new Uint8Array(16));
  o[6] = (o[6] & 0x0f) | 0x40;
  o[8] = (o[8] & 0x3f) | 0x80;
  const h = [...o].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/* ------------------------------------------------------------------------- */
/* Les scans qui ne peuvent pas partir d'ici                                 */
/* ------------------------------------------------------------------------- */

/**
 * Le bandeau permanent des scans d'une autre source restés sur le téléphone (une autre école,
 * Gestion, une entrée sans école) : leur nombre, et pour les premiers leur numéro et leur heure.
 * Jamais effacés sans le dire (SCAN-QR § 5.2). `null` s'il n'y en a pas.
 */
export function bandeauDesOrphelines(orphelines: readonly EntreeDeFile[], combien = 5): { texte: string; detail: string } | null {
  const scans = orphelines.filter((e) => e.mode !== 'annulation');
  if (scans.length === 0) return null;
  const n = scans.length;
  const depuisGestion = scans.every((e) => estSourceDeGestion(e.source));
  const texte = depuisGestion
    ? `${n} scan${n > 1 ? 's' : ''} d’une autre école attend${n > 1 ? 'ent' : ''} sur ce téléphone : ouvrez cette école pour ${n > 1 ? 'les ' : 'l’'}envoyer.`
    : `${n} scan${n > 1 ? 's' : ''} d’un ancien lien ou d’une autre école n’${n > 1 ? 'ont' : 'a'} pas pu partir : ouvrez le lien de leur école sur ce téléphone, ou prévenez la coordination.`;
  const detail = scans
    .slice(0, combien)
    .map((e) => {
      const numero = e.qr ? lireEtiquette(e.qr) : null;
      const qui = numero ? `n° ${numeroEnClair(numero)}` : 'enfant cherché';
      const t = Date.parse(e.momentAppareil) + (Number.isFinite(e.ecartMs) ? e.ecartMs : 0);
      const heure = Number.isFinite(t) ? heureEnClair(new Date(t).toISOString()) : '';
      return heure ? `${qui} à ${heure}` : qui;
    })
    .join(' · ');
  return { texte, detail: n > combien ? `${detail} · …` : detail };
}

/** Le bandeau d'un lien mort (remplacé, expiré, bloqué) quand des scans attendent encore ; `null` sinon. */
export function bandeauDuLienMort(lienMort: boolean, enAttente: number): string | null {
  if (!lienMort || enAttente === 0) return null;
  return `Ce lien n’est plus valable : ${enAttente} scan${enAttente > 1 ? 's restent' : ' reste'} sur ce téléphone. Ouvrez ici le nouveau lien de l’école : ${enAttente > 1 ? 'ils partiront' : 'il partira'} avec lui.`;
}

/** Ce que dit le compteur « en attente » : jamais « 0 » quand le stockage n'a pas pu être relu. */
export function texteEnAttente(enAttente: number, lisible: boolean): string {
  return lisible ? `${enAttente} en attente` : 'file illisible : rechargez la page (les scans sont gardés)';
}
